import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button, Input, Radio, Space } from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
} from "@ant-design/icons";

const PdfUploader = () => {
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [radioSelect, setRadioSelect] = useState("judge");

  // ✅ password สำหรับ read_statement
  const [statementPassword, setStatementPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSource, setPasswordSource] = useState(null); // 'filename' | 'manual' | null

  const [viewMode, setViewMode] = useState("upload");
  const [taskId, setTaskId] = useState(null);
  const [originalResult, setOriginalResult] = useState(null);
  const [editedJsonStr, setEditedJsonStr] = useState("");
  const [jsonError, setJsonError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);

      // ✅ ถ้าเป็น read_statement → ลองดึงรหัสจากชื่อไฟล์อัตโนมัติ
      if (radioSelect === "readMoney") {
        const nameWithoutExt = file.name.replace(/\.pdf$/i, "");
        if (nameWithoutExt.includes("-")) {
          const guessed = nameWithoutExt.split("-")[1];
          setStatementPassword(guessed);
          setPasswordSource("filename");
        } else {
          setStatementPassword("");
          setPasswordSource(null);
        }
      }

      return () => URL.revokeObjectURL(url);
    }
  }, [file, radioSelect]);

  // ✅ reset password เมื่อเปลี่ยน doc_type
  useEffect(() => {
    if (radioSelect !== "readMoney") {
      setStatementPassword("");
      setPasswordSource(null);
    }
  }, [radioSelect]);

  const onFileChange = (e) => setFile(e.target.files?.[0] || null);

  const cancelReview = () => {
    setViewMode("upload");
    setOriginalResult(null);
    setEditedJsonStr("");
    setTaskId(null);
    setFile(null);
    setPdfUrl(null);
    setStatementPassword("");
    setPasswordSource(null);
  };

  const uploadToApi = async () => {
    if (!file || !radioSelect) {
      alert("กรุณาเลือกไฟล์ PDF และเลือกประเภท ก่อนครับ");
      return;
    }

    // ✅ ถ้าเป็น read_statement และยังไม่มีรหัส → กัน
    if (radioSelect === "readMoney" && !statementPassword.trim()) {
      alert("กรุณากรอกรหัสผ่านสำหรับไฟล์ Statement ก่อนครับ");
      return;
    }

    const docTypeMap = {
      receipt: "lawsuit_receipt",
      tr14: "tr14",
      read: "read_pdf",
      judge: "court_enforce",
      readMoney: "read_statement",
      settlement: "settlement",
    };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docTypeMap[radioSelect]);

    // ✅ ส่ง password ไปกับ form
    if (radioSelect === "readMoney" && statementPassword.trim()) {
      formData.append("password", statementPassword.trim());
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "http://localhost:8000/api/v1/extract",
        formData,
        { headers: { "X-API-KEY": "Nickie_Secret_2026" } },
      );

      const apiResult = response.data;
      setOriginalResult(apiResult);
      setTaskId(apiResult.task_id || apiResult.id || "ไม่มี Task ID");

      const dataToShow =
        apiResult.data !== undefined ? apiResult.data : apiResult;
      setEditedJsonStr(JSON.stringify(dataToShow, null, 2));
      setViewMode("review");
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;

      // ✅ ถ้า backend แจ้งว่ารหัสผิด → ให้ user แก้โดยไม่ต้อง reload
      if (typeof detail === "string" && detail.includes("รหัสผ่าน")) {
        setStatementPassword("");
        setPasswordSource(null);
        alert(`🔐 ${detail}\nกรุณากรอกรหัสผ่านใหม่อีกครั้ง`);
      } else {
        alert("ส่งข้อมูลไม่สำเร็จ เช็ค API Key หรือสถานะ Server ดูครับ");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJsonChange = (e) => {
    setEditedJsonStr(e.target.value);
    setJsonError("");
  };

  const handleApprove = async () => {
    let parsedJson;
    try {
      parsedJson = JSON.parse(editedJsonStr);
    } catch {
      setJsonError(
        "❌ รูปแบบ JSON ไม่ถูกต้อง กรุณาตรวจสอบลูกน้ำ (,) หรือเครื่องหมายปีกกาอีกครั้ง",
      );
      return;
    }

    if (!taskId || taskId === "ไม่มี Task ID") {
      alert("ไม่พบ Task ID ในระบบ ไม่สามารถอัปเดตข้อมูลลงฐานข้อมูลได้");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `http://localhost:8000/api/v1/tasks/${taskId}/approve`,
        { human_corrected_json: parsedJson, status: "human_verified" },
        {
          headers: {
            "X-API-KEY": "Nickie_Secret_2026",
            "Content-Type": "application/json",
          },
        },
      );
      alert("✅ บันทึกข้อมูลและยืนยันสำเร็จ!");
      cancelReview();
    } catch (error) {
      console.error(
        "เกิดข้อผิดพลาดในการ Approve:",
        error.response?.data || error.message,
      );
      alert("บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  // =================================================================
  // UI — Upload Screen
  // =================================================================
  if (viewMode === "upload") {
    return (
      <div style={styles.container}>
        <h2>ระบบเคลียร์เบิกทดลองคำฟ้อง (Admin Mode)</h2>

        <div style={styles.uploadCard}>
          <Radio.Group
            onChange={(e) => setRadioSelect(e.target.value)}
            value={radioSelect}
            size="large"
            style={{
              display: "flex",
              gap: 24,
              flexWrap: "wrap",
              padding: "10px 0",
            }}
          >
            <Radio value="receipt" style={{ fontSize: 16 }}>
              🤝 อ่านใบเสร็จ
            </Radio>
            <Radio value="tr14" style={{ fontSize: 16 }}>
              👁️ อ่านทร.14/1
            </Radio>
            <Radio value="read" style={{ fontSize: 16 }}>
              👁️ อ่านอย่างเดียว
            </Radio>
            <Radio value="readMoney" style={{ fontSize: 16 }}>
              🏦 อ่าน Statement
            </Radio>
            <Radio value="judge" style={{ fontSize: 16 }}>
              ⚖️ อ่าน/บันทึกพิพากษา
            </Radio>
            <Radio value="settlement" style={{ fontSize: 16 }}>
              ⚖️ อ่าน/บันทึกทำยอม
            </Radio>
          </Radio.Group>

          <Input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={onFileChange}
            style={styles.fileInput}
          />

          {/* ✅ Password Block — แสดงเฉพาะ read_statement */}
          {radioSelect === "readMoney" && (
            <div style={styles.passwordBlock}>
              <div style={styles.passwordLabel}>
                <LockOutlined style={{ marginRight: 6 }} />
                รหัสผ่านไฟล์ PDF
                {passwordSource === "filename" && (
                  <span style={styles.passwordBadge}>
                    🔍 ดึงจากชื่อไฟล์อัตโนมัติ
                  </span>
                )}
              </div>

              <div style={styles.passwordInputRow}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={statementPassword}
                  onChange={(e) => {
                    setStatementPassword(e.target.value);
                    setPasswordSource("manual");
                  }}
                  placeholder="กรอกรหัสผ่าน เช่น 440001"
                  style={styles.passwordInput}
                />
                <button
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.eyeButton}
                  title={showPassword ? "ซ่อนรหัส" : "แสดงรหัส"}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>

              <div style={styles.passwordHint}>
                {passwordSource === "filename"
                  ? `ระบบพบ "_" ในชื่อไฟล์ "${file?.name}" จึงดึงรหัส "${statementPassword}" มาให้อัตโนมัติ — แก้ได้หากไม่ถูกต้อง`
                  : 'รหัสผ่านจะถูกส่งไปให้ ระบบ ปลดล็อก PDF — หากไม่มีรหัส ลองตั้งชื่อไฟล์เป็น "รหัส_ชื่อไฟล์.pdf"'}
              </div>
            </div>
          )}

          <Button
            onClick={uploadToApi}
            disabled={loading}
            style={styles.primaryButton}
          >
            {loading ? "กำลังให้ AI ประมวลผล..." : "ส่งไฟล์อ่านข้อมูล"}
          </Button>
        </div>
      </div>
    );
  }

  // =================================================================
  // UI — Review Screen
  // =================================================================
  return (
    <div style={styles.reviewContainer}>
      <div style={styles.headerBar}>
        <h3 style={{ margin: 0 }}>🔍 ตรวจสอบความถูกต้อง (Task ID: {taskId})</h3>
        <Space>
          <Button onClick={cancelReview} danger>
            ยกเลิก
          </Button>
          <Button
            onClick={handleApprove}
            disabled={
              saving ||
              radioSelect === "read" ||
              radioSelect === "readMoney" ||
              radioSelect === "receipt"
            }
            type="primary"
          >
            {saving ? "กำลังบันทึก..." : "✅ ยืนยันข้อมูล (Approve)"}
          </Button>
        </Space>
      </div>

      <div style={styles.splitWorkspace}>
        <div style={styles.pdfPane}>
          <div style={styles.paneHeader}>📄 ต้นฉบับเอกสาร PDF</div>
          {pdfUrl ? (
            <iframe src={pdfUrl} style={styles.iframe} title="PDF Viewer" />
          ) : (
            <div style={styles.noData}>ไม่สามารถโหลด PDF ได้</div>
          )}
        </div>

        <div style={styles.editorPane}>
          <div style={styles.paneHeader}>
            ✏️ ข้อมูลที่ AI สกัดได้ (แก้ให้ถูกต้องได้เลย)
          </div>

          <textarea
            style={styles.textarea}
            value={editedJsonStr}
            onChange={handleJsonChange}
            spellCheck="false"
          />

          {jsonError && <div style={styles.errorBox}>{jsonError}</div>}

          <div style={styles.debugInfo}>
            {originalResult?.data?.needs_review && (
              <div
                style={{
                  color: "#e67e22",
                  fontWeight: "bold",
                  marginBottom: 8,
                }}
              >
                ⚠️ AI แนะนำให้ตรวจสอบ:{" "}
                {originalResult.data.review_reasons?.join(", ")}
              </div>
            )}
            <small>
              <strong>Confidence Score:</strong>{" "}
              {originalResult?.confidence || "-"}
            </small>
            <br />
            <small>
              <strong>File Name:</strong> {file?.name}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "30px",
    maxWidth: "600px",
    margin: "40px auto",
    border: "1px solid #e1e4e8",
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    fontFamily: "sans-serif",
  },
  uploadCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "20px",
  },
  fileInput: {
    padding: "10px",
    border: "1px dashed #ccc",
    borderRadius: "8px",
    cursor: "pointer",
  },

  // ── Password Block ──────────────────────────────────────
  passwordBlock: {
    border: "1px solid #d0e4ff",
    borderRadius: "8px",
    backgroundColor: "#f0f7ff",
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  passwordLabel: {
    fontWeight: "bold",
    fontSize: "14px",
    color: "#1a56db",
    display: "flex",
    alignItems: "center",
  },
  passwordBadge: {
    marginLeft: "8px",
    fontSize: "12px",
    backgroundColor: "#d1fae5",
    color: "#065f46",
    padding: "2px 8px",
    borderRadius: "999px",
    fontWeight: "normal",
  },
  passwordInputRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  passwordInput: {
    flex: 1,
    padding: "8px 12px",
    fontSize: "15px",
    border: "1px solid #93c5fd",
    borderRadius: "6px",
    outline: "none",
    fontFamily: "monospace",
    letterSpacing: "0.1em",
    backgroundColor: "#fff",
  },
  eyeButton: {
    padding: "8px 12px",
    border: "1px solid #93c5fd",
    borderRadius: "6px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "16px",
    color: "#1a56db",
    display: "flex",
    alignItems: "center",
  },
  passwordHint: {
    fontSize: "12px",
    color: "#4b5563",
    lineHeight: "1.5",
  },

  // ── Review Screen ───────────────────────────────────────
  reviewContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100vw",
    position: "fixed",
    top: 0,
    left: 0,
    backgroundColor: "#f6f8fa",
    fontFamily: "sans-serif",
    zIndex: 9999,
  },
  headerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e1e4e8",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  splitWorkspace: { display: "flex", flex: 1, overflow: "hidden" },
  pdfPane: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    borderRight: "2px solid #e1e4e8",
  },
  editorPane: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
  },
  paneHeader: {
    padding: "12px 20px",
    backgroundColor: "#f1f3f5",
    borderBottom: "1px solid #e1e4e8",
    fontWeight: "bold",
    fontSize: "14px",
    color: "#495057",
  },
  iframe: { width: "100%", height: "100%", border: "none" },
  textarea: {
    flex: 1,
    padding: "20px",
    fontSize: "14px",
    fontFamily: "monospace",
    border: "none",
    resize: "none",
    outline: "none",
    backgroundColor: "#282c34",
    color: "#abb2bf",
    lineHeight: "1.5",
  },
  errorBox: {
    padding: "12px",
    backgroundColor: "#ffe3e3",
    color: "#c92a2a",
    borderTop: "1px solid #ffa8a8",
    fontSize: "14px",
  },
  debugInfo: {
    padding: "12px 20px",
    borderTop: "1px solid #e1e4e8",
    backgroundColor: "#f8f9fa",
    color: "#868e96",
  },
  primaryButton: {
    padding: "12px 24px",
    backgroundColor: "#0366d6",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "16px",
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#adb5bd",
  },
};

export default PdfUploader;
